import {
    UseMutationResult,
    useMutation,
    useQueryClient,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { SEARCH_QUERY_KEY, GANTT_SEARCH_QUERY_KEY } from '../baselineTasks';
import {
    BulkPushdownFiltersRequest,
    BulkPushdownFiltersResponse,
    bulkPushdownFiltersResponseSchema,
    BulkLocationAttribFiltersResponse,
    bulkLocationAttribFiltersResponseSchema,
    BulkLocationPushdownSearchRequest,
    BulkLocationPushdownSearchNoProjectRequest,
    BulkLocationPushdownSearchResponse,
    bulkLocationPushdownSearchResponseSchema,
    BulkPushdownRequest,
    BulkPushdownResponse,
    bulkPushdownResponseSchema,
} from './bulkPushdown.types';

const FILTERS_QUERY_KEY = 'BulkPushdownFilters';
const ATTRIB_FILTERS_QUERY_KEY = 'BulkLocationAttribFilters';
const LOCATION_SEARCH_QUERY_KEY = 'BulkLocationPushdownSearch';
const LOCATION_SEARCH_NO_PROJECT_QUERY_KEY = 'BulkLocationPushdownSearchNoProject';

// ---- Load Bulk Pushdown Filters ----

const loadBulkPushdownFilters = async (request: BulkPushdownFiltersRequest): Promise<BulkPushdownFiltersResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-pushdown-filters`, { json: request })
            .json();
        const parsed = bulkPushdownFiltersResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('loadBulkPushdownFilters: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }], isProjectsActive: false, isProjectIdRequiredForLocationTask: false, projects: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('loadBulkPushdownFilters API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to load bulk pushdown filters: ${error.message}`);
        }
        throw new Error('Failed to load bulk pushdown filters: Unknown error');
    }
};

export const useLoadBulkPushdownFilters = (
    baselineId: string,
    options?: { enabled?: boolean }
): UseQueryResult<BulkPushdownFiltersResponse> => {
    return useQuery<BulkPushdownFiltersResponse>({
        queryKey: [FILTERS_QUERY_KEY, baselineId],
        queryFn: () => loadBulkPushdownFilters({ baselineId }),
        enabled: options?.enabled ?? true,
    });
};

// ---- Load Bulk Location Attribute Filters ----

const loadBulkLocationAttribFilters = async (): Promise<BulkLocationAttribFiltersResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-location-attrib-filters`)
            .json();
        const parsed = bulkLocationAttribFiltersResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('loadBulkLocationAttribFilters: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }], areas: [], brands: [], continents: [], countries: [], locationTypes: [], managementCompanies: [], regions: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('loadBulkLocationAttribFilters API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to load bulk location attribute filters: ${error.message}`);
        }
        throw new Error('Failed to load bulk location attribute filters: Unknown error');
    }
};

export const useLoadBulkLocationAttribFilters = (
    options?: { enabled?: boolean }
): UseQueryResult<BulkLocationAttribFiltersResponse> => {
    return useQuery<BulkLocationAttribFiltersResponse>({
        queryKey: [ATTRIB_FILTERS_QUERY_KEY],
        queryFn: loadBulkLocationAttribFilters,
        enabled: options?.enabled ?? true,
    });
};

// ---- Bulk Location Pushdown Search (with project) ----

const bulkLocationPushdownSearch = async (request: BulkLocationPushdownSearchRequest): Promise<BulkLocationPushdownSearchResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-location-pushdown-search`, { json: request })
            .json();
        const parsed = bulkLocationPushdownSearchResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('bulkLocationPushdownSearch: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }], locations: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('bulkLocationPushdownSearch API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to search locations: ${error.message}`);
        }
        throw new Error('Failed to search locations: Unknown error');
    }
};

export const useBulkLocationPushdownSearch = (
    request: BulkLocationPushdownSearchRequest,
    options?: { enabled?: boolean }
): UseQueryResult<BulkLocationPushdownSearchResponse> => {
    return useQuery<BulkLocationPushdownSearchResponse>({
        queryKey: [LOCATION_SEARCH_QUERY_KEY, request],
        queryFn: () => bulkLocationPushdownSearch(request),
        enabled: options?.enabled ?? true,
    });
};

// ---- Bulk Location Pushdown Search (no project) ----

const bulkLocationPushdownSearchNoProject = async (
    request: BulkLocationPushdownSearchNoProjectRequest
): Promise<BulkLocationPushdownSearchResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-location-pushdown-search-no-project`, { json: request })
            .json();
        const parsed = bulkLocationPushdownSearchResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('bulkLocationPushdownSearchNoProject: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }], locations: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('bulkLocationPushdownSearchNoProject API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to search locations: ${error.message}`);
        }
        throw new Error('Failed to search locations: Unknown error');
    }
};

export const useBulkLocationPushdownSearchNoProject = (
    request: BulkLocationPushdownSearchNoProjectRequest,
    options?: { enabled?: boolean }
): UseQueryResult<BulkLocationPushdownSearchResponse> => {
    return useQuery<BulkLocationPushdownSearchResponse>({
        queryKey: [LOCATION_SEARCH_NO_PROJECT_QUERY_KEY, request],
        queryFn: () => bulkLocationPushdownSearchNoProject(request),
        enabled: options?.enabled ?? true,
    });
};

// ---- Bulk Pushdown ----

const bulkPushdown = async (request: BulkPushdownRequest): Promise<BulkPushdownResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-pushdown`, { json: request })
            .json();
        const parsed = bulkPushdownResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('bulkPushdown: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
        }
        return parsed.data;
    } catch (error) {
        console.error('bulkPushdown API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to perform bulk pushdown: ${error.message}`);
        }
        throw new Error('Failed to perform bulk pushdown: Unknown error');
    }
};

export const useBulkPushdown = (
    callback?: (results: BulkPushdownResponse) => void
): UseMutationResult<BulkPushdownResponse, Error, BulkPushdownRequest> => {
    const queryClient = useQueryClient();
    return useMutation<BulkPushdownResponse, Error, BulkPushdownRequest>({
        mutationFn: bulkPushdown,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);

            if (!mutationResponse.isError) {
                queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [GANTT_SEARCH_QUERY_KEY] });
            }
        },
    });
};

export * from './bulkPushdown.types';
